import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { SOLUTION } from '../../constants/execution-modes';

import styled from 'styled-components';

import { initializeApplication } from '../../actions/application';
import { getHomework } from '../../actions/homework';
import { resumeLogin } from '../../actions/auth';
import { onPlayVideo } from '../../actions/media-player';

import Button from '../../components/button';
import { getCorrectAnswers } from '../../lib/exercise';
import { useMathJaxStatus } from '../../lib/hooks';
import { getSourceLabel, setPageTitle } from '../../lib/utils';
import CorrectionLoadError from '../../components/correction-load-error';
import CorrectionResults from '../../components/correction-results';
import { SOURCES } from '../../constants/sources';

export const ButtonsContainer = styled.div`
    display: flex;

    @media print {
        display: none;
    }
`;

const Container = styled.div`
    margin: 42px;
    @media print {
        margin: 10px;
    }
`;

export const CorrectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 8px 0;
`;

export const CorrectionTitle = styled.span`
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 28px;
`;

export const ExerciseId = styled.span`
    font-size: 14px;
    letter-spacing: 0.16px;
    line-height: 20px;
`;

export const NumberExercises = styled.span`
    font-size: 16px;
    letter-spacing: 0;
    line-height: 24px;
`;

const SolutionContainer = ({
    exercises,
    getHomework,
    geogebraExercises,
    homeworkId,
    initializeApplication,
    isLtiLaunch,
    publicTitle,
    resumeLogin,
    onPlayVideo,
    source,
    videos,
    executionMode,
}) => {
    const [expand, setExpand] = useState(true);
    const cleanHomeworkId = homeworkId && homeworkId.split('_').length === 3 ? homeworkId.split('_')[2] : homeworkId;
    const isMathJaxLoading = useMathJaxStatus();

    useEffect(() => {
        const initData = async () => {
            const result = await initializeApplication(SOLUTION);
            if (!!result.profile) {
                await resumeLogin(source === SOURCES.LAZE && isLtiLaunch);
                if (result.profile.originalId) {
                    await getHomework(result.profile, {}, true);
                }
            } else {
                throw result;
            }
        };

        initData().catch(() => {});

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPageTitle(source, publicTitle);
    }, [source, publicTitle]);

    const answers = exercises.map((exercise) => getCorrectAnswers(exercise));

    if (executionMode !== SOLUTION) {
        return (
            <CorrectionLoadError title="Oh no, qualcosa è andato storto!" showButtons={true}>
                <br />
                {`Torna su ${getSourceLabel(source)} e cerca di nuovo la prova.`}
                <br />
                {'Hai bisogno di aiuto? Contattaci'}
            </CorrectionLoadError>
        );
    }

    return (
        <Container>
            <ExerciseId>{`(ID): #${cleanHomeworkId}`}</ExerciseId>
            <CorrectionHeader>
                <CorrectionTitle>{publicTitle}</CorrectionTitle>
                <NumberExercises>{`${exercises.length} esercizi`}</NumberExercises>

                <ButtonsContainer>
                    <Button
                        type="tertiary"
                        onClick={() => {
                            window.print();
                        }}
                        size="small"
                        disabled={isMathJaxLoading}
                    >
                        {'Stampa'}
                    </Button>

                    <Button type="tertiary" onClick={() => setExpand(!expand)} size="small">
                        {expand ? 'NASCONDI TUTTO' : 'VEDI TUTTO'}
                    </Button>
                </ButtonsContainer>
            </CorrectionHeader>
            <CorrectionResults
                answers={answers}
                exercises={exercises}
                filter="ALL"
                geogebraExercises={geogebraExercises}
                expand={expand}
                solution={true}
                videos={videos}
                onPlayVideo={onPlayVideo}
            />
        </Container>
    );
};

SolutionContainer.propTypes = {
    exercises: PropTypes.array,
    getHomework: PropTypes.func,
    initializeApplication: PropTypes.func,
    publicTitle: PropTypes.string,
    geogebraExercises: PropTypes.array,
    homeworkId: PropTypes.string,
    isLtiLaunch: PropTypes.bool,
    resumeLogin: PropTypes.func,
    onPlayVideo: PropTypes.func,
    source: PropTypes.string,
    videos: PropTypes.array,
    executionMode: PropTypes.string,
};

const mapStateToProps = (state) => {
    return {
        exercises: state.homework.exercises,
        publicTitle: state.homework.publicTitle,
        geogebraExercises: state.homework.geogebraExercises,
        homeworkId: state.homework.id,
        isLtiLaunch: state.application.isLtiLaunch,
        source: state.application.source,
        videos: state.mediaPlayer.videos,
        executionMode: state.application.executionMode,
    };
};

const actionCreators = {
    getHomework,
    initializeApplication,
    resumeLogin,
    onPlayVideo,
};

export default connect(mapStateToProps, actionCreators)(SolutionContainer);
